import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  Host,
  input,
  Optional,
  output,
  SkipSelf,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  ColumnBase,
  ColumnComponent,
  IdService,
} from '@progress/kendo-angular-grid';

import { IGroupingExSelectionChangeArgs } from '../Grouping/grouping-ex.selection.directive';

/**
 * A shared Kendo grid column that supplies the repeated group-header, header and
 * filter-menu templates the sparta grid pages declare on nearly every column, so a
 * column declaration collapses from ~50 lines to a handful of attributes (WI 31909).
 *
 * It extends {@link ColumnComponent} and re-provides {@link ColumnBase} with
 * `useExisting`, the same mechanism `kendo-grid-column-ex` uses so Kendo's grid
 * `ContentChildren<ColumnBase>` sees it as a real column while it stays a direct
 * child of `<kendo-grid>`.
 *
 * Every default template is content-overridable: if the consumer projects the
 * matching `*kendoGrid...Template`, that wins; otherwise the built-in default is
 * used. The cell is the common override (e.g. an editable `ups-labor-type-text-cell`).
 *
 * Scope: variant 1 (string filter, group-selection header without a page output).
 * Variant 2 (checkbox filter + `groupSelectChecked` wired to a page handler) is
 * tracked in the follow-up WI and is served by the passthrough output below.
 */
@Component({
  selector: 'ups-grid-code-column',
  providers: [
    {
      provide: ColumnBase,
      useExisting: forwardRef(() => UpsGridCodeColumnComponent),
    },
  ],
  templateUrl: './grid-code-column.component.html',
  standalone: false,
  // OnPush per Feature 31662. This component's default group-header hosts the
  // still-Default ggx-group-selection-header, which trips the gate's
  // onpush-ancestor-default-child rule; that child is used by ~52 templates
  // across 7 apps so it is converted bottom-up under WI 34141, which removes the
  // override below.
  // onpush-gate-override: Tim Baldwin, call 71006280, 2026-09-23 03:48Z, scope UpsGridCodeColumnComponent only (hosts Default ggx-group-selection-header); follow-up WI 34141
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpsGridCodeColumnComponent extends ColumnComponent {
  /** Human-readable label shown in the group header (falls back to `field`). */
  readonly groupLabel = input<string>('');

  /** Passthrough of the group-selection checkbox event for pages that track selection. */
  readonly groupSelectChecked = output<IGroupingExSelectionChangeArgs>();

  @ViewChild('defaultGroupHeaderTemplate', { static: true })
  private defaultGroupHeaderTemplate!: TemplateRef<unknown>;
  @ViewChild('defaultHeaderTemplate', { static: true })
  private defaultHeaderTemplate!: TemplateRef<unknown>;
  @ViewChild('defaultCellTemplate', { static: true })
  private defaultCellTemplate!: TemplateRef<unknown>;
  @ViewChild('defaultFilterMenuTemplate', { static: true })
  private defaultFilterMenuTemplate!: TemplateRef<unknown>;

  constructor(
    _el: ElementRef,
    @Optional() @Host() @SkipSelf() parent?: ColumnBase,
    @Optional() idService?: IdService
  ) {
    super(parent, idService);
  }

  // Content-wins-else-default. Kendo reads these getters when it renders the column;
  // if the consumer projected the matching template the base class exposes it here,
  // otherwise we hand Kendo our built-in default.

  // Return type matches the Kendo base accessors (TemplateRef<any>) so these are
  // clean TS overrides, no suppression needed.
  override get templateRef(): TemplateRef<any> {
    return this.template?.templateRef ?? this.defaultCellTemplate;
  }

  override get headerTemplateRef(): TemplateRef<any> {
    return super.headerTemplateRef ?? this.defaultHeaderTemplate;
  }

  override get groupHeaderTemplateRef(): TemplateRef<any> {
    return (
      this.groupHeaderTemplate?.templateRef ?? this.defaultGroupHeaderTemplate
    );
  }

  override get filterMenuTemplateRef(): TemplateRef<any> {
    return (
      this.filterMenuTemplate?.templateRef ?? this.defaultFilterMenuTemplate
    );
  }
}
